import { Link } from "react-router-dom";
import {
  ArrowRightIcon,
  BagIcon,
  BuildingIcon,
  HeadphonesIcon,
  RefreshIcon,
} from "../components/Icons";
import { GridPattern } from "../components/Patterns";
import { usePageMeta } from "../seo";

const solutions = [
  {
    to: "/solutions/dtc",
    Icon: BagIcon,
    eyebrow: "Direct-to-consumer",
    title: "Answer order questions with the order in view.",
    body: "Shopify orders next to every ticket, saved replies for refunds and delivery questions, and email, WhatsApp and calls in one inbox.",
    bullets: [
      "Shopify sidebar: orders, fulfillment, LTV",
      "Saved replies with the customer's name filled in",
      "Email, WhatsApp and phone together",
    ],
  },
  {
    to: "/solutions/b2b",
    Icon: BuildingIcon,
    eyebrow: "B2B & wholesale",
    title: "Support for business customers.",
    body: "Group people into companies by email domain, set SLA targets by priority, and show account data from your own system on every ticket.",
    bullets: [
      "Companies by email domain",
      "First-reply and resolution targets by priority",
      "Customer data lookup from your system",
    ],
  },
  {
    to: "/solutions/marketplaces",
    Icon: HeadphonesIcon,
    eyebrow: "Marketplaces",
    title: "Buyers and sellers, kept apart.",
    body: "A queue per support address, rules that tag and assign on arrival, saved replies with your policy wording, and internal notes for the reasoning.",
    bullets: [
      "Queues and rules",
      "Policy wording in saved replies",
      "Internal notes and attachments",
    ],
  },
  {
    to: "/solutions/subscription",
    Icon: RefreshIcon,
    eyebrow: "Subscription & recurring",
    title: "The subscriber's plan, on the ticket.",
    body: "Show plan and renewal details from your own billing system with the customer data lookup, and hand tickets to it with ticket actions.",
    bullets: [
      "Plan and renewal from your system",
      "Ticket actions like \"Pause subscription\"",
      "Rules that tag cancellation requests",
    ],
  },
];

export default function Solutions() {
  usePageMeta("/solutions");
  return (
    <>
      <section className="relative overflow-hidden bg-ink-900 text-ink-100">
        <div className="pointer-events-none absolute inset-0 text-white/[0.04]">
          <GridPattern />
        </div>
        <div className="pointer-events-none absolute -top-24 -right-32 h-[28rem] w-[28rem] rounded-full bg-brand-500/25 blur-3xl" />
        <div className="pointer-events-none absolute top-40 -left-24 h-80 w-80 rounded-full bg-violet-500/20 blur-3xl" />
        <div className="relative mx-auto max-w-6xl px-4 pt-16 pb-12 sm:px-6 sm:pt-20">
          <p className="mb-3 animate-fade-up text-xs font-semibold uppercase tracking-wider text-brand-300">
            Solutions
          </p>
          <h1
            className="animate-fade-up text-4xl font-semibold tracking-tight text-white sm:text-5xl md:text-6xl"
            style={{ animationDelay: "60ms" }}
          >
            Same ZopDesk. <br className="hidden sm:block" />
            Used the way <em className="not-italic text-brand-300">your team</em> sells.
          </h1>
          <p
            className="mt-6 max-w-3xl animate-fade-up text-lg text-ink-300"
            style={{ animationDelay: "140ms" }}
          >
            There are no special editions. These pages show which of today's
            features matter most for four common kinds of business, and how
            you'd set them up.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-4 py-20 sm:px-6">
        <div className="grid gap-6 md:grid-cols-2">
          {solutions.map(({ to, Icon, eyebrow, title, body, bullets }, i) => (
            <Link
              key={to}
              to={to}
              className="group relative flex animate-fade-up flex-col overflow-hidden rounded-2xl border border-ink-200 bg-white p-7 shadow-sm transition hover:-translate-y-0.5 hover:border-brand-200 hover:shadow-md"
              style={{ animationDelay: `${i * 70}ms` }}
            >
              <div className="mb-5 flex items-center gap-3">
                <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-brand-500 to-violet-500 text-white shadow-md shadow-brand-500/30">
                  <Icon width={22} height={22} />
                </div>
                <p className="text-xs font-semibold uppercase tracking-wider text-brand-700">
                  {eyebrow}
                </p>
              </div>
              <h2 className="text-xl font-semibold tracking-tight text-ink-900 sm:text-2xl">
                {title}
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-ink-600">{body}</p>
              <ul className="mt-5 space-y-2 text-sm text-ink-700">
                {bullets.map((b) => (
                  <li key={b} className="flex items-start gap-2">
                    <span className="mt-1 inline-flex h-1.5 w-1.5 flex-none rounded-full bg-brand-500" />
                    {b}
                  </li>
                ))}
              </ul>
              <span className="mt-6 inline-flex items-center gap-1 text-sm font-semibold text-brand-700 transition group-hover:gap-2">
                Explore {eyebrow.toLowerCase()}
                <ArrowRightIcon width={14} height={14} />
              </span>
              <div className="pointer-events-none absolute -bottom-16 -right-16 h-40 w-40 rounded-full bg-brand-500/5 blur-3xl transition group-hover:bg-brand-500/10" />
            </Link>
          ))}
        </div>
      </section>

      <section className="relative overflow-hidden bg-ink-900 text-ink-100">
        <div className="pointer-events-none absolute top-0 left-1/2 h-96 w-96 -translate-x-1/2 rounded-full bg-brand-500/20 blur-3xl" />
        <div className="relative mx-auto max-w-5xl px-4 py-16 text-center sm:px-6 sm:py-20">
          <h2 className="text-3xl font-semibold tracking-tight text-white sm:text-4xl">
            Not sure which fits?
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-lg text-ink-300">
            Tell us how your team works. We'll tell you how we'd set ZopDesk
            up, or whether it isn't the right fit yet.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link
              to="/contact"
              className="group inline-flex items-center gap-2 rounded-md bg-gradient-to-br from-brand-500 to-violet-500 px-5 py-3 text-sm font-semibold text-white shadow-lg shadow-brand-500/30 transition hover:from-brand-400 hover:to-violet-400"
            >
              Talk to us
              <ArrowRightIcon
                width={16}
                height={16}
                className="transition-transform group-hover:translate-x-0.5"
              />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
